
import { ReactNode, useEffect, useState, useCallback } from "react";
import { AuthContext } from "./authContext";
import { useAuthSession } from "./useAuthSession";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSessionRefresh } from "./hooks/useSessionRefresh";
import { useProfileManagement } from "./hooks/useProfileManagement";
import { useAuthActions } from "./hooks/useAuthActions";
import { checkSessionStatus } from "@/services/auth/sessionUtils";

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const { session, user, profile, isLoading, setProfile, setIsLoading } = useAuthSession();
  const [forceLogoutTriggered, setForceLogoutTriggered] = useState(false);
  const [healthCheckRunning, setHealthCheckRunning] = useState(false);
  
  // Use our custom hooks
  const { sessionRefreshAttempts, setSessionRefreshAttempts, refreshSession } = useSessionRefresh();
  const { refreshProfile, updateUserProfile } = useProfileManagement(user, setProfile, setIsLoading);
  const { signUp, signIn, signOut } = useAuthActions();

  // Handle session refresh failure
  useEffect(() => {
    if (sessionRefreshAttempts >= 3) {
      console.warn(`${sessionRefreshAttempts} consecutive session refresh failures - signing out`);
      toast.error("Unable to maintain your session. Please log in again.");
      forceLogout();
    }
  }, [sessionRefreshAttempts]);

  // Add safety timeout for loading state
  useEffect(() => {
    let timeoutId: number;
    
    if (isLoading) {
      timeoutId = window.setTimeout(() => {
        console.warn("Authentication loading state timeout reached - forcing reset");
        setIsLoading(false);
        
        // If we have a user but no profile, try to refresh the profile
        if (user && !profile) {
          console.log("User exists but profile is missing, trying to refresh profile");
          refreshProfile();
        }
      }, 10000); // 10 seconds timeout
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, setIsLoading, user, profile, refreshProfile]);

  // Add periodic session health check
  const runSessionHealthCheck = useCallback(async () => {
    if (healthCheckRunning || !user || !session) return;
    
    try {
      setHealthCheckRunning(true);
      console.log("Running session health check");
      
      const { valid, expiresIn } = await checkSessionStatus();
      
      if (!valid) {
        console.warn("Session invalid in health check");
        const success = await refreshSession();
        
        if (!success) {
          console.error("Failed to refresh invalid session");
          // Don't force logout immediately, increment attempts
          setSessionRefreshAttempts(prev => prev + 1);
        } else {
          console.log("Successfully refreshed session in health check");
          setSessionRefreshAttempts(0);
          
          // Refresh profile if missing
          if (!profile) {
            console.log("Profile missing, refreshing after session refresh");
            await refreshProfile();
          }
        }
      } else if (expiresIn && expiresIn < 300) {
        // Refresh session if it expires in less than 5 minutes
        console.log(`Session expires soon (${expiresIn}s), refreshing`);
        await refreshSession();
      } else {
        console.log(`Session is healthy${expiresIn ? `, expires in ${expiresIn}s` : ''}`);
      }
    } catch (error) {
      console.error("Error in session health check:", error);
    } finally {
      setHealthCheckRunning(false);
    }
  }, [healthCheckRunning, user, session, profile, refreshSession, refreshProfile, setSessionRefreshAttempts]);

  // Run session health check periodically
  useEffect(() => {
    const healthCheckInterval = setInterval(() => {
      runSessionHealthCheck();
    }, 2 * 60 * 1000); // Every 2 minutes
    
    return () => clearInterval(healthCheckInterval);
  }, [runSessionHealthCheck]);

  // Force logout function - now doesn't directly use navigate
  const forceLogout = async () => {
    console.log("Force logout triggered");
    
    if (forceLogoutTriggered) {
      console.log("Force logout already in progress, skipping");
      return;
    }
    
    setForceLogoutTriggered(true);
    
    try {
      // Clear all auth-related storage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          localStorage.removeItem(key);
        }
      }
      
      // Reset all state
      setSessionRefreshAttempts(0);
      setIsLoading(false);
      setProfile(null);
      
      // Call Supabase signOut
      await supabase.auth.signOut({ scope: 'local' });
      
      toast.success("You have been logged out");
      
      // Use window.location.href as a fallback for navigation
      window.location.href = '/login';
    } catch (error) {
      console.error("Force logout error:", error);
      // If normal logout fails, force page reload as last resort
      window.location.href = '/login';
    } finally {
      setForceLogoutTriggered(false);
    }
  };

  // Log auth state changes for debugging
  useEffect(() => {
    console.log("AuthProvider state:", { 
      isAuthenticated: !!user, 
      profileLoaded: !!profile,
      isAdmin: profile?.is_admin === true,
      isAdminFromProfile: profile?.is_admin,
      isLoading,
      profileData: profile
    });
  }, [user, profile, isLoading]);

  // Combined refresh session method that also refreshes profile if needed
  const combinedRefreshSession = async (): Promise<boolean> => {
    const success = await refreshSession();
    if (success && user) {
      await refreshProfile();
    }
    return success;
  };

  // Ensure isAdmin is derived correctly from profile
  const isAdmin = profile?.is_admin === true;

  const value = {
    session,
    user,
    profile,
    isLoading,
    signUp,
    signIn,
    signOut,
    isAdmin,
    refreshProfile,
    refreshSession: combinedRefreshSession,
    updateUserProfile,
    forceLogout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
